import { useEffect, useState } from 'react';
import { Building2, Star, Link2, MousePointerClick, Inbox, KanbanSquare } from 'lucide-react';
import { useAdminApi } from '@/hooks/use-admin';
import { DirectoryManager } from './directory/DirectoryManager';
import { InvestorForm } from './directory/InvestorForm';

const StatTile = ({ label, value, icon: Icon, testid }) => (
  <div className="rounded-xl border border-border bg-card px-4 py-3" data-testid={testid}>
    <div className="flex items-center justify-between">
      <p className="text-[10px] font-grotesk uppercase tracking-[0.14em] text-muted-foreground">{label}</p>
      <Icon className="h-3.5 w-3.5 text-muted-foreground" />
    </div>
    <p className="font-display text-2xl mt-1 tabular-nums">{value}</p>
  </div>
);

const InvestorStats = () => {
  const api = useAdminApi();
  const [s, setS] = useState(null);
  useEffect(() => { api.get('/directory/investors/stats').then(setS).catch(() => {}); }, [api]);
  if (!s) return null;
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6 gap-3 mb-7" data-testid="investors-stats">
      <StatTile label="Investors" value={s.total} icon={Building2} testid="investors-stat-total" />
      <StatTile label="Featured" value={s.featured} icon={Star} testid="investors-stat-featured" />
      <StatTile label="With apply form" value={s.with_form} icon={Link2} testid="investors-stat-with-form" />
      <StatTile label="Apply clicks" value={s.apply_clicks} icon={MousePointerClick} testid="investors-stat-clicks" />
      <StatTile label="Applications" value={s.applications} icon={Inbox} testid="investors-stat-applications" />
      <StatTile label="In pipelines" value={s.in_pipelines} icon={KanbanSquare} testid="investors-stat-pipelines" />
    </div>
  );
};

const config = {
  collection: 'investors',
  title: 'Investors',
  heading: 'Build the investor desk.',
  subtitle: 'These profiles power the Investor Connect directory on /app/investors — founders browse them, pick up contacts and add them to their pipeline. Changes appear instantly.',
  singular: 'investor',
  formWidth: 'max-w-2xl',
  FormComponent: InvestorForm,
  StatsComponent: InvestorStats,
  validate: (form) => (!String(form.name || '').trim() ? 'Investor / Fund name is required' : null),
  searchKeys: ['name', 'poc_name', 'fund_type', 'role', 'city', 'state', 'thesis', 'stage_focus', 'sector_focus', 'portfolio'],
  fields: [
    { key: 'avatar', label: 'Logo', type: 'image' },
    { key: 'name', label: 'Investor / Fund name', type: 'text', required: true },
    { key: 'fund_type', label: 'Fund type', type: 'text' },
    { key: 'poc_name', label: 'POC name', type: 'text' },
    { key: 'role', label: 'POC role', type: 'text' },
    { key: 'email', label: 'Email', type: 'text' },
    { key: 'country_code', label: 'Phone country code', type: 'text' },
    { key: 'phone', label: 'Phone', type: 'text' },
    { key: 'state', label: 'State', type: 'text' },
    { key: 'city', label: 'City', type: 'text' },
    { key: 'check_size', label: 'Check size', type: 'text' },
    { key: 'stage_focus', label: 'Stage focus', type: 'tags' },
    { key: 'sector_focus', label: 'Sector focus', type: 'tags' },
    { key: 'thesis', label: 'Thesis', type: 'textarea' },
    { key: 'apply_url', label: 'Application form link', type: 'text' },
    { key: 'linkedin', label: 'LinkedIn', type: 'text' },
    { key: 'website', label: 'Website', type: 'text' },
    { key: 'portfolio', label: 'Notable portfolio', type: 'tags' },
    { key: 'featured', label: 'Featured', type: 'boolean' },
  ],
  card: (i) => ({
    title: i.name,
    subtitle: [i.poc_name, i.fund_type || i.firm].filter(Boolean).join(' · '),
    avatar: i.avatar,
    badges: [
      i.check_size || i.ticket,
      [i.city, i.state].filter(Boolean).join(', ') || i.location,
      ...(Array.isArray(i.stage_focus) ? i.stage_focus.slice(0, 2) : []),
      ...(Array.isArray(i.sector_focus) ? i.sector_focus.slice(0, 2) : []),
    ],
  }),
};

const AdminInvestors = () => <DirectoryManager config={config} />;
export default AdminInvestors;
